import './ArsStatisticsOrganizationModal.scss';

import {CloseOutlined} from '@ant-design/icons';
import {Empty} from 'antd';
import cn from 'classnames';
import dayjs from 'dayjs';
import React, {useEffect, useId} from 'react';
import {useSelector} from 'react-redux';

import AntButton from '../../../../../components/AntButton/AntButton';
import AntTable from '../../../../../components/AntTable/AntTable';
import * as localizationKeys from '../../../../../consts/localization/localizationKeys';
import {ARS_STATISTIC_ORGANIZATION_MODAL_TYPE} from '../../../../../consts/modals/modalTypes';
import useIsMobileViewport from '../../../../../hooks/useIsMobileViewport';
import {getLocalizedStrings} from '../../../../../services/localization/localizationService';
import {hideModalAction} from '../../../../../state/ducks/modal';
import {makeSelectArsOrganizationStatistics} from '../../../../../state/selectors/services';
import {dispatch} from '../../../../../state/store';
import helpers from '../../../../../utils/helpers';
import arsStatisticsOrganizationModalService from '../ArsStatisticsOrganizationModal/arsStatisticsOrganizationModalService';

const ArsStatisticsOrganizationModal = (props) => {
    const isMobile = useIsMobileViewport();
    const titleId = useId();
    const localizedStrings = getLocalizedStrings();

    const resultStatusMap = {
        1: localizedStrings[localizationKeys.SERVICE_ARS_STATUS_MESSAGE_1],
        2: localizedStrings[localizationKeys.SERVICE_ARS_STATUS_MESSAGE_2],
        3: localizedStrings[localizationKeys.SERVICE_ARS_STATUS_MESSAGE_3],
        4: localizedStrings[localizationKeys.SERVICE_ARS_STATUS_MESSAGE_4],
        5: localizedStrings[localizationKeys.SERVICE_ARS_STATUS_MESSAGE_5],
    };
    const {organization, buildingsStatistics = []} = useSelector(makeSelectArsOrganizationStatistics()) || {};

    const {onCancel} = props;

    const handleCancel = () => {
        helpers.runFunction(onCancel);
        dispatch(hideModalAction(ARS_STATISTIC_ORGANIZATION_MODAL_TYPE));
    };

    const StatisticsInfoList = buildingsStatistics.map((item) => {
        const resultStatusText = resultStatusMap[item?.status];
        let dateInfo = '-';

        if (item?.dateLocal && !item.dateLocal.startsWith('0001-') && dayjs(item.dateLocal).isValid()) {
            const dateString = item?.dateLocal;

            dateInfo = dayjs(dateString).format('YYYY-MM-DD, HH:mm');
        }

        return {
            dateInfo,
            dateLocal: item?.dateLocal,
            buildingAddress: item?.buildingAddress,
            status: resultStatusText,
        };
    });

    const tableColumns = arsStatisticsOrganizationModalService.getStatisticsOrganizationColumns();

    const cancelButtonText = localizedStrings[localizationKeys.INFO_MODAL_BUTTON_TEXT];
    const titleTextContent = `${localizedStrings[localizationKeys.ARS_INFO_MODAL_TITLE_TEXT]}`;

    useEffect(() => {
        document.body.classList.add('ars-statistics-modal-open');

        return () => document.body.classList.remove('ars-statistics-modal-open');
    }, []);

    useEffect(() => {
        if (!isMobile) return undefined;

        const dialog = document.getElementById(titleId)?.closest('[role="dialog"]');

        dialog?.setAttribute('aria-labelledby', titleId);
        return () => dialog?.removeAttribute('aria-labelledby');
    }, [isMobile, titleId]);

    if (isMobile) {
        return (
            <div className='service-ars-statistics'>
                <header>
                    <div>
                        <h2 id={titleId}>{titleTextContent}</h2>
                        <p>{organization}</p>
                    </div>
                    <button
                        type='button'
                        aria-label={localizedStrings[localizationKeys.SERVICES_MOBILE_CLOSE]}
                        onClick={handleCancel}
                    >
                        <CloseOutlined aria-hidden />
                    </button>
                </header>
                <main>
                    {StatisticsInfoList.length ? (
                        StatisticsInfoList.map((building, index) => (
                            <article key={`${building.buildingAddress}-${index}`}>
                                <h3>{building.buildingAddress || '\u2014'}</h3>
                                <p>{building.status || '\u2014'}</p>
                                <time dateTime={building.dateInfo === '-' ? undefined : building.dateLocal}>
                                    {building.dateInfo === '-'
                                        ? '\u2014'
                                        : dayjs(building.dateLocal).format('DD.MM.YYYY \u00b7 HH:mm')}
                                </time>
                            </article>
                        ))
                    ) : (
                        <Empty />
                    )}
                </main>
                <footer>
                    <button type='button' onClick={handleCancel}>
                        {cancelButtonText}
                    </button>
                </footer>
            </div>
        );
    }

    return (
        <div className='ars-statistics-modal-content flex-column m-auto px-[25px] py-5'>
            <p className='ars-statistics-modal-title my-4 text-center text-24 font-semibold'>
                {titleTextContent}
                <span className='ml-2'>{`"${organization}"`}</span>
            </p>
            <div className='ars-statistics-modal-body max-h-[55vh]'>
                {!StatisticsInfoList.length ? (
                    <Empty className={'mt-4'} />
                ) : (
                    <>
                        <div className='ars-statistics-desktop-table hidden md:block'>
                            <AntTable
                                bordered
                                showPagination={false}
                                scroll={{
                                    y: 350,
                                }}
                                dataSource={StatisticsInfoList}
                                columns={tableColumns}
                            />
                        </div>

                        <div className='ars-statistics-mobile-list flex min-w-0 flex-col gap-3 md:hidden'>
                            {StatisticsInfoList.map((building, index) => (
                                <article
                                    key={`${building.buildingAddress}-${building.dateInfo}-${index}`}
                                    className='min-w-0 rounded-10 border border-divider bg-white p-3'
                                >
                                    {tableColumns.map((column) => (
                                        <div key={column.key} className='ars-statistics-mobile-field min-w-0'>
                                            <div className='mb-0.5 text-11 font-semibold uppercase tracking-05 text-santa'>
                                                {column.title}
                                            </div>
                                            <div className='min-w-0 break-words text-13 leading-18 text-eerie'>
                                                {column.render
                                                    ? column.render(building[column.dataIndex], building)
                                                    : building[column.dataIndex]}
                                            </div>
                                        </div>
                                    ))}
                                </article>
                            ))}
                        </div>
                    </>
                )}
            </div>
            <div className={cn('ars-statistics-modal-actions flex justify-center')}>
                <AntButton className='ars-statistics-modal-button mt-5 h-[50px]' onClick={handleCancel}>
                    {cancelButtonText}
                </AntButton>
            </div>
        </div>
    );
};

export default React.memo(ArsStatisticsOrganizationModal);
